import { type Attrs, MinzeElement, Reactive, EventListeners } from "minze";
import { analyticsTrackNoPrefix } from "../../../modules/analytics";
import {
  parseInlineStyle,
  pathToString,
  closestInPath,
} from "../../../modules/utils";

export class TjButton extends MinzeElement {
  reactive: Reactive = [
    ["background", "#068FC3"],
    ["color", "#fff"],
  ];
  attrs: Attrs = [
    "size",
    "inner-style",
    "textAlign",
    "display",
    "alignItems",
    "justifyContent",
    "boxShadow",
    "flex",
    "border",
    "borderRadius",
    "borderBottom",
    "borderTop",
    "borderLeft",
    "borderRight",
    "marginBottom",
    "marginLeft",
    "width",
    "height",
    "fontSize",
    "fontWeight",
    "padding",
    "disabled",
  ];

  eventListeners: EventListeners = [[this, "click", this.onClick.bind(this)]];

  html = () => `
    <button class="button" type="${this.type}" ${this.disabled ? "disabled" : ""}>
      <slot></slot>
    </button>
  `;

  onClick(event: any) {
    let path = event.composedPath();
    let form = closestInPath("FORM", path);
    analyticsTrackNoPrefix({
      event: "custom.click",
      elementClasses: this.className,
      elementId: this.id,
      element: this,
      elementUrl: this.href || this.action || "",
      elementTarget: this.target || "",
      clickText: this.innerText,
      originalEvent: event,
      elementValue: this.value || "",
      elementType: this.type || "",
      path: pathToString(path),
      formId: form ? form.id : "",
      formAction: form ? form.action : "",
      formClasses: form ? form.className : "",
    });
  }

  css = () => {
    this.heightValue = this.height ? this.height : "auto";
    this.borderRadiusValue = this.borderRadius ? this.borderRadius : "12px";
    this.fontSizeValue = this.fontSize
      ? this.fontSize
      : this.size === "small"
        ? "16px"
        : "22px";
    this.paddingValue = this.padding
      ? this.padding
      : this.size === "small"
        ? "8px 25px"
        : "14px 40px";

    return /*css*/`
      :host {
        ${this.flex ? `flex: ${this.flex};` : ""}
      }

      .button {
        width: ${this.width || 'auto'};
        ${this.display ? `display: ${this.display};` : ""}
        ${this.alignItems ? `align-items: ${this.justifyContent};` : ""}
        ${this.justifyContent ? `justify-content: ${this.justifyContent};` : ""}
        ${this.textAlign ? `text-align: ${this.textAlign};` : ""}
        background: ${this.bg || this.background};
        color: ${this.textColor || this.color};
        font-size: ${this.fontSizeValue || '16px'};
        font-weight: ${this.fontWeight || 700};
        border: ${this.border || "none"};
        ${this.borderRadiusValue ? `border-radius: ${this.borderRadiusValue};` : ""}
        ${this.borderRadius ? `border-radius: ${this.borderRadius};` : ""}
        padding: ${this.paddingValue};
        cursor: pointer;
        transition: filter 0.1s ease-in-out, box-shadow 200ms ease, transform 0.1s;
        ${this.heightValue ? `height: ${this.heightValue};` : ""}
        ${this.height ? `height: ${this.height};` : ""}
        border-bottom: ${this.borderBottom || 'none'};
        border-top: ${this.borderTop || 'none'};
        border-left: ${this.borderLeft || 'none'};
        border-right: ${this.borderRight || 'none'};
        margin-bottom: ${this.marginBottom || '0px'};
        margin-left: ${this.marginLeft || '0px'};
        box-shadow: ${this.boxShadow || 'none'};
        font-family: inherit;
        ${this.textDecoration ? `text-decoration: ${this.textDecoration};` : ""}

        line-height: 150%;
      }

      button:disabled,
      button[disabled] {
        background: #DDDDDD;
        pointer-events: none;
      }

      .button > * {
        font-size: ${this.fontSize};
        font-weight: ${this.fontWeight};
      }

      .button:hover {
        box-shadow: 0px 4.2px 4.2px 0px rgba(0, 0, 0, 0.15);
        transform: scale(1.05);
      }

      ${parseInlineStyle(this.innerStyle as string)}
    `;
  };
}
